import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import { useARGuideOptional } from '../../contexts/ARGuideContext';
import type { GuideStep } from '../../types/arGuides';
import { CacheService } from '../../utils/cache';
import { useMenuItems, NAV_GROUP_ORDER, NAV_GROUP_LABELS } from '../../configs/menuConfig';
import type { NavGroup, MenuItem } from '../../configs/menuConfig';
import { Bill, Money, ChartDown, Home, Logout } from '../icons/IconLibrary';
import { cn } from '../ui/cn';

// Convierte una ruta en un id de ancla estable: '/animals/new' -> 'animals-new', '/' -> 'home'
const pathToAnchor = (p: string) => {
  const token = p.split('/').filter(Boolean).join('-');
  return token || 'home';
};

const itemAnchor = (item: MenuItem) => item.anchor ?? `menu-${pathToAnchor(item.path)}`;

// Categorías del menú abiertas: arrancan todas cerradas y se recuerda lo que
// abre o cierra el usuario (el Sidebar se vuelve a montar en cada página).
export const SIDEBAR_GROUPS_OPEN_KEY = 'mcd_sidebar_groups_open';

const leerGruposAbiertos = (): NavGroup[] => {
  try {
    const guardado = JSON.parse(window.localStorage.getItem(SIDEBAR_GROUPS_OPEN_KEY) ?? '[]');
    return Array.isArray(guardado) ? guardado.filter((g): g is NavGroup => NAV_GROUP_ORDER.includes(g)) : [];
  } catch {
    return [];
  }
};

// Selectores que resalta el paso actual de una guía (tutorial). Si apuntan a un
// item de una categoría cerrada, la abrimos mientras dure el paso.
const selectoresDelPaso = (step: GuideStep | undefined): string[] =>
  (step?.elements ?? []).flatMap((e) => {
    const el = e as { targetSelector?: unknown; position?: { near?: unknown } };
    return [el.targetSelector, el.position?.near].filter((s): s is string => typeof s === 'string');
  });

interface SidebarProps {
  /** Modo colapsado: riel de íconos sin texto (solo desktop). */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onCloseMobile?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggleCollapse, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { can, isSiteAdmin } = usePermissions();
  const { menuItems, quickActions } = useMenuItems();
  const { openModal } = useModal();
  const { signOut } = useAuth();
  // Solapas de /devtools: "Herramientas" (sin tab), "Empresas y usuarios" y "Opiniones".
  const devtoolsTab =
    location.pathname === '/devtools' ? new URLSearchParams(location.search).get('tab') : undefined;
  const isEmpresasAdmin = devtoolsTab === 'empresas';
  const isOpinionesAdmin = devtoolsTab === 'opiniones';
  const isDevtoolsHerramientas = location.pathname === '/devtools' && !isEmpresasAdmin && !isOpinionesAdmin;

  // Cierre de sesión: vuelve siempre a la página de entrada (landing pública).
  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Agrupa los items por sección (preserva el orden de NAV_GROUP_ORDER), excluyendo "Inicio".
  const sections = useMemo(() => {
    const map = new Map<NavGroup, MenuItem[]>();
    for (const item of menuItems) {
      if (item.path === '/') continue; // Inicio va en el header (logo clickeable)
      const g = item.group ?? 'principal';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(item);
    }
    return NAV_GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ group: g, items: map.get(g)! }));
  }, [menuItems]);

  // Índice de la sección que contiene el item activo (para destacar su header).
  const activeSectionIdx = sections.findIndex((s) => s.items.some((item) => item.path === location.pathname));

  const [gruposAbiertos, setGruposAbiertos] = useState<NavGroup[]>(leerGruposAbiertos);
  const alternarGrupo = (group: NavGroup) => {
    setGruposAbiertos((prev) => {
      const next = prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group];
      try {
        window.localStorage.setItem(SIDEBAR_GROUPS_OPEN_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const guia = useARGuideOptional();
  const selectoresGuia = selectoresDelPaso(guia?.activeGuide?.steps[guia.currentStepIndex]);
  const abiertaPorGuia = (items: MenuItem[]) =>
    items.some((item) => {
      const anchor = itemAnchor(item);
      return selectoresGuia.some((s) => s.includes(`"${anchor}"`) || s.includes(`'${anchor}'`));
    });

  const handleQuickAction = (actionPath: string) => {
    switch (actionPath) {
      case '/animals/new':
        CacheService.remove('campoDefault');
        CacheService.remove('lastFechaEvento');
        navigate(actionPath);
        break;
      default:
        navigate(actionPath);
    }
  };

  const sidebarOnlyActions = [
    {
      id: 'ingreso',
      label: 'Cargar Ingreso',
      module: 'financial' as const,
      action: 'create' as const,
      icon: <Bill size={18} />,
      onClick: () => navigate('/administracion/ingresos'),
    },
    {
      id: 'gasto',
      label: 'Cargar Gasto/Compra',
      module: 'financial' as const,
      action: 'create' as const,
      icon: <Money size={18} />,
      onClick: () => openModal('GASTO'),
    },
    {
      id: 'consumo',
      label: 'Uso de Inventario',
      module: 'financial' as const,
      action: 'edit' as const,
      icon: <ChartDown size={18} />,
      onClick: () => openModal('CONSUMO'),
    },
  ].filter((a) => can(a.module, a.action));

  // ---- MODO COLAPSADO: riel de íconos sin texto ----
  if (collapsed) {
    return (
      <div className="flex h-full flex-col items-center gap-1 px-1 py-3">
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            aria-label="Expandir menú"
            title="Expandir menú"
            className="mb-1 flex h-9 w-9 items-center justify-center rounded-md text-white/60 transition-colors hover:bg-forest-mid/40 hover:text-white"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {sections.map(({ items }, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div className="my-1 h-px w-6 bg-forest-mid/30" />}
            {items.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-ar-guide={itemAnchor(item)}
                  title={item.label}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                    active ? 'bg-forest-mid/25 text-brote' : 'text-white/70 hover:bg-forest-mid/20 hover:text-white',
                  )}
                >
                  {item.icon}
                </Link>
              );
            })}
          </React.Fragment>
        ))}

        {quickActions.length > 0 && <div className="my-1 h-px w-6 bg-forest-mid/30" />}
        {quickActions.map((action) => (
          <Link
            key={action.path}
            to={action.path}
            data-ar-guide={`qa-${pathToAnchor(action.path)}`}
            title={action.label}
            onClick={(e) => {
              if (action.path === '/animals/new') {
                e.preventDefault();
                handleQuickAction(action.path);
              }
            }}
            className="flex h-9 w-9 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-forest-mid/20 hover:text-white"
          >
            {action.icon}
          </Link>
        ))}
        {sidebarOnlyActions.map((action) => (
          <button
            key={action.id}
            onClick={action.onClick}
            title={action.label}
            data-ar-guide={`menu-quick-${action.id}`}
            className="flex h-9 w-9 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-forest-mid/20 hover:text-white"
          >
            {action.icon}
          </button>
        ))}

        {isSiteAdmin && (
          <>
            <div className="my-1 h-px w-6 bg-danger/30" />
            <Link
              to="/admin/fotos-referencia"
              title="Fotos de referencia"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md text-base transition-colors',
                location.pathname === '/admin/fotos-referencia' ? 'bg-danger/15' : 'text-danger/70 hover:bg-danger/10',
              )}
            >
              📷
            </Link>
            <Link
              to="/admin/observatorio"
              title="Observatorio de puntos de control"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md text-base transition-colors',
                location.pathname === '/admin/observatorio' ? 'bg-danger/15' : 'text-danger/70 hover:bg-danger/10',
              )}
            >
              🔭
            </Link>
            <Link
              to="/devtools"
              title="Herramientas de Desarrollo"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                isDevtoolsHerramientas ? 'bg-danger/15 text-white' : 'text-danger/70 hover:bg-danger/10 hover:text-white',
              )}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
            <Link
              to="/devtools?tab=empresas"
              title="Empresas y usuarios"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                isEmpresasAdmin ? 'bg-danger/15 text-white' : 'text-danger/70 hover:bg-danger/10 hover:text-white',
              )}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </Link>
            <Link
              to="/devtools?tab=opiniones"
              title="Opiniones"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                isOpinionesAdmin ? 'bg-danger/15 text-white' : 'text-danger/70 hover:bg-danger/10 hover:text-white',
              )}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinejoin="round" strokeWidth={2} d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5z" />
              </svg>
            </Link>
          </>
        )}

        {/* Pie: página de entrada + cierre de sesión, fijos abajo del riel */}
        <div className="mt-auto flex w-full flex-col items-center gap-1 border-t border-white/10 pt-2">
          <Link
            to="/"
            data-ar-guide="sidebar-inicio-publico"
            aria-label="Página de inicio"
            title="Página de inicio"
            className="flex h-9 w-9 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-forest-mid/20 hover:text-white"
          >
            <Home size={18} />
          </Link>
          <button
            onClick={handleSignOut}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            className="flex h-9 w-9 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-danger/15 hover:text-white"
          >
            <Logout size={18} />
          </button>
        </div>
      </div>
    );
  }

  // ---- MODO EXPANDIDO ----
  return (
    <div className="flex h-full flex-col">
      {/* Barra superior: toggle de colapso (desktop) + cierre (mobile) */}
      <div className="flex items-center justify-between px-3 pt-4 pb-2">
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            aria-label="Contraer menú"
            title="Contraer menú"
            className="group hidden w-full items-center gap-2 rounded-lg bg-forest-mid/20 px-3 py-2 text-white/80 transition-all hover:bg-forest-mid/40 hover:text-white md:flex"
          >
            <svg className="h-4 w-4 transition-transform group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-xs font-semibold uppercase tracking-wider">Contraer menú</span>
            <div className="ml-auto flex gap-0.5">
              <div className="h-1 w-1 rounded-pixel bg-white/30" />
              <div className="h-1 w-1 rounded-pixel bg-white/30" />
              <div className="h-1 w-1 rounded-pixel bg-white/30" />
            </div>
          </button>
        )}
        {onCloseMobile && (
          <button
            onClick={onCloseMobile}
            aria-label="Cerrar menú"
            className="rounded-md p-1.5 text-white/70 transition-colors hover:bg-forest-mid/40 hover:text-white md:hidden"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="mcd-scroll-fade flex-1 overflow-y-auto px-3 pb-4">
        <nav className="space-y-1">
          {sections.map(({ group, items }, sectionIdx) => (
            <details key={group} open={gruposAbiertos.includes(group) || abiertaPorGuia(items)} className="group">
              <summary
                data-ar-guide={`menu-group-${group}`}
                onClick={(e) => {
                  e.preventDefault();
                  alternarGrupo(group);
                }}
                className="flex cursor-pointer list-none items-center gap-2 px-2 py-2 transition-colors hover:bg-white/5 [&::-webkit-details-marker]:hidden">
                {/* Barra decorativa — brote en la sección activa, forest-mid en el resto */}
                <div className={`h-3.5 w-1 rounded-sm transition-colors ${sectionIdx === activeSectionIdx ? 'bg-brote' : 'bg-forest-mid/60'}`} />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">{NAV_GROUP_LABELS[group]}</span>
                <svg className="ml-auto h-3.5 w-3.5 text-white/30 transition-transform duration-200 group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </summary>
              <ul className="mt-0.5 space-y-0.5 border-l border-white/10 ml-1.5 pl-2">
                {items.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        data-ar-guide={itemAnchor(item)}
                        className={cn(
                          'group/item flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all',
                          active ? 'bg-brote/15 font-medium text-white shadow-sm' : 'text-white/75 hover:bg-white/5 hover:text-white',
                        )}
                      >
                        <span className={cn('transition-colors', active ? 'text-brote' : 'text-white/45 group-hover/item:text-white/70')}>{item.icon}</span>
                        <span className="truncate">{item.label}</span>
                        {active && <span className="ml-auto h-1.5 w-1.5 rounded-pixel bg-brote" />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </details>
          ))}
        </nav>

        {/* Acciones rápidas */}
        {(quickActions.length > 0 || sidebarOnlyActions.length > 0) && (
          <div className="mt-4">
            <div className="flex items-center gap-2 px-2 py-2" data-ar-guide="menu-group-acciones">
              <div className="h-3.5 w-1 rounded-sm bg-brote/50" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">Acciones</span>
            </div>
            <ul className="mt-0.5 space-y-0.5 border-l border-white/10 ml-1.5 pl-2">
              {quickActions.map((action) => (
                <li key={action.path}>
                  <Link
                    to={action.path}
                    data-ar-guide={`qa-${pathToAnchor(action.path)}`}
                    onClick={(e) => {
                      if (action.path === '/animals/new') {
                        e.preventDefault();
                        handleQuickAction(action.path);
                      }
                    }}
                    className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-white/75 transition-all hover:bg-white/5 hover:text-white"
                  >
                    <span className="text-white/45 transition-colors group-hover:text-white/70">{action.icon}</span>
                    <span className="truncate">{action.label}</span>
                  </Link>
                </li>
              ))}
              {sidebarOnlyActions.map((action) => (
                <li key={action.id}>
                  <button
                    onClick={action.onClick}
                    data-ar-guide={`menu-quick-${action.id}`}
                    className="group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-white/75 transition-all hover:bg-white/5 hover:text-white"
                  >
                    <span className="text-white/45 transition-colors group-hover:text-white/70">{action.icon}</span>
                    <span className="truncate">{action.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Dev Tools - Site Admin Only */}
        {isSiteAdmin && (
          <div className="mt-4">
            <div className="flex items-center gap-2 px-2 py-2">
              <div className="h-3.5 w-1 rounded-sm bg-danger/50" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-danger/60">Dev tools</span>
            </div>
            <ul className="mt-0.5 space-y-0.5 border-l border-danger/20 ml-1.5 pl-2">
              <li>
                <Link
                  to="/admin/fotos-referencia"
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all',
                    location.pathname === '/admin/fotos-referencia' ? 'bg-danger/15 font-medium text-white' : 'text-white/75 hover:bg-danger/10 hover:text-white',
                  )}
                >
                  <span className={location.pathname === '/admin/fotos-referencia' ? 'text-danger' : 'text-danger/50'}>📷</span>
                  <span>Fotos de referencia</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/admin/observatorio"
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all',
                    location.pathname === '/admin/observatorio' ? 'bg-danger/15 font-medium text-white' : 'text-white/75 hover:bg-danger/10 hover:text-white',
                  )}
                >
                  <span className={location.pathname === '/admin/observatorio' ? 'text-danger' : 'text-danger/50'}>🔭</span>
                  <span>Observatorio de puntos</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/devtools"
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all',
                    isDevtoolsHerramientas ? 'bg-danger/15 font-medium text-white' : 'text-white/75 hover:bg-danger/10 hover:text-white',
                  )}
                >
                  <span className={isDevtoolsHerramientas ? 'text-danger' : 'text-danger/50'}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                  <span className="truncate">Herramientas de Desarrollo</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/devtools?tab=empresas"
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all',
                    isEmpresasAdmin ? 'bg-danger/15 font-medium text-white' : 'text-white/75 hover:bg-danger/10 hover:text-white',
                  )}
                >
                  <span className={isEmpresasAdmin ? 'text-danger' : 'text-danger/50'}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </span>
                  <span className="truncate">Empresas y usuarios</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/devtools?tab=opiniones"
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all',
                    isOpinionesAdmin ? 'bg-danger/15 font-medium text-white' : 'text-white/75 hover:bg-danger/10 hover:text-white',
                  )}
                >
                  <span className={isOpinionesAdmin ? 'text-danger' : 'text-danger/50'}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinejoin="round" strokeWidth={2} d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5z" />
                    </svg>
                  </span>
                  <span className="truncate">Opiniones</span>
                </Link>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Pie fijo: vuelta a la página de entrada + cierre de sesión (siempre visibles) */}
      <div className="border-t border-white/10 px-3 py-3">
        <ul className="space-y-0.5">
          <li>
            <Link
              to="/"
              data-ar-guide="sidebar-inicio-publico"
              className="group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-white/75 transition-all hover:bg-white/5 hover:text-white"
            >
              <span className="text-white/45 transition-colors group-hover:text-white/70">
                <Home size={18} />
              </span>
              <span className="truncate">Página de inicio</span>
            </Link>
          </li>
          <li>
            <button
              onClick={handleSignOut}
              className="group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-white/75 transition-all hover:bg-danger/10 hover:text-white"
            >
              <span className="text-white/45 transition-colors group-hover:text-white/70">
                <Logout size={18} />
              </span>
              <span className="truncate">Cerrar sesión</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
